import { IconButton } from '@material-ui/core';
import ConsumerRoutePath from 'app/consumer-app/ConsumerRoutePath';
import { UrlQueryKey } from 'app/core/api/api-core/api-constants';
import { useSearchProductCatalogV2 } from 'app/core/api/product/api-product';
import { Product } from 'app/core/api/product/api-product.types';
import Button from 'app/shared/components/button/Button';
import FormTextInput from 'app/shared/components/form-input/FormTextInput';
import useInputForm from 'app/shared/components/form-input/utilities/use-input-form';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import Body from 'app/shared/components/typography/body/Body';
import { useViewport } from 'app/shared/context-hooks/use-viewport.hook';
import Form from 'app/shared/frames/Form';
import ClassName from 'app/styles/ClassName';
import styledMixins from 'app/styles/styledMixins';
import { useQueryParams } from 'app/tools/hooks/misc-hooks';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components/macro';
import { useDebouncedCallback } from 'use-debounce';
import Tile from '../../../shared/frames/Tile';
import ProductCard from './components/ProductCard';

const NoResults = styled(Body)`
  ${styledMixins.flexCenter}
  align-self: stretch;
  height: 1px;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
`;

const ResultTile = styled(Tile).attrs({
  wide: true,
  className: ClassName.NO_AUTO_MARGIN,
})``;

const RESULTS = 'results';
type LocationState = { [x in typeof RESULTS]?: Product[] };
const isLocationState = (input: unknown): input is LocationState => {
  const results = _.get(input as LocationState, RESULTS);
  return (
    _.isArray(results) &&
    !_.isEmpty(results) &&
    results.every(v => typeof _.get(v, 'id') === 'number')
  );
};

type FormValues = {
  search: string;
};

export default function ProductSearchCatalog(): JSX.Element {
  const queryParams = useQueryParams();
  const history = useHistory();
  const location = useLocation();
  const encodedUrlSearch = queryParams.get(UrlQueryKey.SEARCH);
  const urlSearchTerms = decodeURIComponent(encodedUrlSearch || '');
  const [searchTerm, setSearchTerm] = useState('');
  const {
    result: newSearchResults,
    request$: requestSearch$,
    error: searchError,
    sending: loading,
  } = useSearchProductCatalogV2({ fresh: true });
  // const [isSearchEnable, setIsSearchEnable] = useState(false);
  const [firstTimeSearch, setFirstTimeSearch] = useState(false);
  const searchResults = useMemo(() => {
    const savedSearchResults = isLocationState(location.state)
      ? location.state.results
      : undefined;
    return newSearchResults || savedSearchResults;
  }, [location.state, newSearchResults]);

  const { inputProps } = useInputForm<FormValues>({
    apiError: searchError,
    defaultValues: useMemo(
      () => ({ sourceCategory: 'products', search: urlSearchTerms }),
      [urlSearchTerms]
    ),
  });

  const requestSearch = () => {
    if (urlSearchTerms?.trim() || searchTerm?.trim()) {
      requestSearch$({
        search: urlSearchTerms?.trim() || searchTerm?.trim(),
      }).subscribe(results => {
        if (urlSearchTerms?.trim() || searchTerm?.trim()) {
          setFirstTimeSearch(false);
        }
        history.replace(location.pathname, { [RESULTS]: results });
      });
    }
  };
  const handleMapMovedDebounced = useDebouncedCallback(
    requestSearch,
    500
  );
  useEffect(() => {
    handleMapMovedDebounced();
  }, [
    handleMapMovedDebounced,
    history,
    location.pathname,
    requestSearch$,
    searchTerm,
    urlSearchTerms,
  ]);

  const { vWidth } = useViewport();
  return (
    <>
      <Form>
        <div className="flex items-right">
          <FormTextInput
            controlName="search"
            optional
            placeholder="Search Product"
            // classicPlaceholder={!isSearchEnable}
            InputProps={{
              inputMode: 'search',
              onChange: evt => {
                setSearchTerm(evt.target.value);
              },
              value: searchTerm,
              endAdornment: (
                <IconButton
                  type="button"
                  disabled={loading}
                  className="text-primary"
                >
                  <Icon icon={IconLib.SEARCH} size={20} />
                </IconButton>
              ),
            }}
            fullWidth
            {...inputProps}
          />
        </div>
      </Form>
      {(searchResults && (
        <>
          <Button
            to={ConsumerRoutePath.MANUAL_PRODUCT_ENTRY}
            startIcon={IconLib.ADD_NEW}
            variant="outlined"
          >
            Add manually instead
          </Button>
          <Body color="primary">Search results</Body>
          {!searchResults.length && !firstTimeSearch && (
            <NoResults>
              *No results found, please add manually instead*
            </NoResults>
          )}
          {!!searchResults.length &&
            searchResults.map(product => (
              <ResultTile
                key={product.id}
                maxHeight={140}
                {...{ vWidth }}
              >
                <ProductCard variant="wide-no-icons" item={product} />
              </ResultTile>
            ))}
        </>
      )) || (
        <Button
          to={ConsumerRoutePath.MANUAL_PRODUCT_ENTRY}
          startIcon={IconLib.ADD_NEW}
          variant="outlined"
        >
          Add manually instead
        </Button>
      )}
    </>
  );
}
